import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { retrouverMaFacture, urlDocument } from '../services/api';
import { useLang } from '../i18n/LanguageContext';

const champ =
  'w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all';
const bouton =
  'inline-flex items-center justify-center rounded-lg bg-[#1F3833] px-5 py-3 font-semibold text-white transition-colors hover:bg-[#2c4f48] disabled:opacity-60';

/**
 * « Retrouver ma facture » — pour l'acheteur SANS compte qui a perdu l'e-mail
 * de confirmation. Numéro de commande (ou de facture) + e-mail d'achat, la
 * même garde que le guide du jeu ; le serveur renvoie les liens signés.
 */
export default function RetrouverFacture() {
  const { t } = useLang();
  const [numero, setNumero] = useState('');
  const [email, setEmail] = useState('');
  const [etat, setEtat] = useState({ enCours: false, erreur: null, resultat: null });

  const chercher = async (e) => {
    e.preventDefault();
    setEtat({ enCours: true, erreur: null, resultat: null });
    try {
      const resultat = await retrouverMaFacture(numero.trim(), email.trim());
      setEtat({ enCours: false, erreur: null, resultat });
    } catch (err) {
      const statut = err.response?.status;
      setEtat({
        enCours: false,
        erreur: statut === 404 ? t('maFacture.introuvable') : statut === 429 ? t('maFacture.tropDEssais') : t('maFacture.erreur'),
        resultat: null,
      });
    }
  };

  const r = etat.resultat;

  return (
    <div className="min-h-[70vh] bg-gradient-to-br from-[#F1F2E9] to-[#F2EFE4] px-4 py-12 md:py-16">
      <SEO title={t('maFacture.seoTitre')} description={t('maFacture.seoDescription')} url="/ma-facture" noindex />

      <div className="mx-auto max-w-xl rounded-2xl bg-white p-6 shadow-sm md:p-8">
        <h1 className="m-0 text-2xl font-black text-[#1F3833] md:text-3xl">{t('maFacture.titre')}</h1>
        <p className="mt-3 text-[#1F3833]/80">{t('maFacture.intro')}</p>

        <form onSubmit={chercher} className="mt-6 flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm font-medium text-[#1F3833]">
            {t('maFacture.champNumero')}
            <input
              className={champ}
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              placeholder="JR-2026-0001"
              autoComplete="off"
              required
            />
            <span className="text-xs font-normal text-gray-500">{t('maFacture.champNumeroAide')}</span>
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium text-[#1F3833]">
            {t('maFacture.champEmail')}
            <input
              type="email"
              className={champ}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <button type="submit" className={bouton} disabled={etat.enCours} data-cta="ma-facture:chercher">
            {etat.enCours ? t('maFacture.recherche') : t('maFacture.bouton')}
          </button>
        </form>

        {etat.erreur && (
          <p role="alert" className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {etat.erreur}
          </p>
        )}

        {r && (
          <div className="mt-6 flex flex-col gap-3 rounded-xl border border-[#DCEAE4] bg-[#F7FAF8] p-5" aria-live="polite">
            {r.facture ? (
              <>
                <p className="m-0 text-[#1F3833]">{t('maFacture.facture', { numero: r.facture.numero })}</p>
                <a
                  href={urlDocument(r.facture.chemin)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={bouton}
                  data-cta="ma-facture:voir"
                >
                  {t('maFacture.voirFacture')}
                </a>
                <p className="m-0 text-xs text-gray-500">{t('maFacture.langueDocument')}</p>
              </>
            ) : r.factureStripeUrl ? (
              <a href={r.factureStripeUrl} target="_blank" rel="noopener noreferrer" className={bouton}>
                {t('maFacture.factureStripe')}
              </a>
            ) : (
              <p className="m-0 text-[#1F3833]">
                {r.enAttenteDePaiement ? t('maFacture.enAttente') : t('maFacture.aucune')}
              </p>
            )}
            {r.noteDeCredit && (
              <a
                href={urlDocument(r.noteDeCredit.chemin)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-red-700 underline"
              >
                {t('maFacture.voirNote', { numero: r.noteDeCredit.numero })}
              </a>
            )}
          </div>
        )}

        <p className="mt-6 text-sm text-gray-600">
          {t('maFacture.compte')}{' '}
          <Link to="/profile/orders" className="font-medium text-[#1F3833] underline">
            {t('compte.profil.mesCommandes')}
          </Link>
        </p>
      </div>
    </div>
  );
}
